import { useState, type FormEvent } from "react";
import type { EnrollmentTokenRequest, EnrollmentTokenResponse } from "../api/types";
import { useCreateEnrollmentToken } from "../hooks/queries";
import { Button } from "../components/ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { useToast } from "../components/ui/Toast";
import { errorMessage } from "../api/errors";
import { formatDateTime, formatRelative } from "../lib/format";

const TTL_DEFAULT = 1440; // 24 saat
const TTL_MAX = 43200; // 30 gün

export function EnrollmentPage() {
  const { toast } = useToast();
  const createToken = useCreateEnrollmentToken();

  const [ttlMinutes, setTtlMinutes] = useState(String(TTL_DEFAULT));
  const [site, setSite] = useState("");
  const [tags, setTags] = useState("");
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  // Token yalnızca gösterim için state'te durur; ekrandan kaldırılınca yok olur.
  const [issued, setIssued] = useState<EnrollmentTokenResponse | null>(null);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const ttl = Number(ttlMinutes);
    if (!Number.isInteger(ttl) || ttl < 1 || ttl > TTL_MAX) {
      setFormError(`TTL 1 ile ${TTL_MAX} dakika arasında bir tam sayı olmalı.`);
      return;
    }
    setFormError(null);
    const body: EnrollmentTokenRequest = {
      ttlMinutes: ttl,
      site: site.trim() || undefined,
      tags: tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean),
      note: note.trim() || undefined,
    };
    createToken.mutate(body, {
      onSuccess: (response) => {
        setIssued(response);
        toast("Enrollment token üretildi — yalnızca bir kez gösterilir.", "success");
      },
      onError: (error) => setFormError(errorMessage(error)),
    });
  }

  async function copyToken() {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.token);
      toast("Token panoya kopyalandı.", "success");
    } catch {
      toast("Kopyalama başarısız — token'ı elle seçip kopyalayın.", "error");
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Ayarlar · Enrollment</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Agent kaydı için tek kullanımlık token üretin. Token yalnızca bir kez
          gösterilir; sunucuda özeti saklanır.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Üretim formu */}
        <Card>
          <CardHeader>
            <CardTitle>Yeni token üret</CardTitle>
            <CardDescription>
              Süre, site ve etiketler token ile kaydolan cihazlara önceden atanır.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="flex flex-col gap-4">
              <Input
                label="Geçerlilik (dakika)"
                type="number"
                min={1}
                max={TTL_MAX}
                required
                hint={`Varsayılan ${TTL_DEFAULT} (24 saat), en fazla ${TTL_MAX} (30 gün)`}
                value={ttlMinutes}
                onChange={(event) => setTtlMinutes(event.target.value)}
              />
              <Input
                label="Site"
                placeholder="ör. İstanbul-Merkez"
                hint="Kayıt olan cihazlara önceden atanır (opsiyonel)"
                value={site}
                onChange={(event) => setSite(event.target.value)}
              />
              <Input
                label="Etiketler"
                placeholder="prod, endpoint"
                hint="Virgülle ayırın (opsiyonel)"
                value={tags}
                onChange={(event) => setTags(event.target.value)}
              />
              <Input
                label="Not"
                placeholder="ör. Ankara şube kurulumu"
                hint="Denetim kaydında görünür"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
              {formError ? (
                <p
                  role="alert"
                  className="bg-danger/10 text-danger rounded-md border border-danger/30 px-3 py-2 text-sm"
                >
                  {formError}
                </p>
              ) : null}
              <Button type="submit" disabled={createToken.isPending}>
                {createToken.isPending ? "Üretiliyor…" : "Token üret"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Tek gösterimlik sonuç */}
        <Card>
          <CardHeader>
            <CardTitle>Üretilen token</CardTitle>
            <CardDescription>
              Bu değer bir daha gösterilmez; kaybetmeden önce kopyalayın.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {issued ? (
              <div className="flex flex-col gap-3">
                <div className="bg-warning/15 text-warning rounded-md border border-warning/40 px-3 py-2 text-sm">
                  <strong>Uyarı:</strong> Bu token <strong>bir daha gösterilmeyecek</strong>.
                  Şimdi kopyalayın ve agent yapılandırmasına yazın.
                </div>
                <div className="flex items-stretch gap-2">
                  <code
                    aria-label="Enrollment token"
                    className="bg-muted text-foreground flex-1 overflow-x-auto rounded-md border border-border px-3 py-2 font-mono text-xs break-all"
                  >
                    {issued.token}
                  </code>
                  <Button variant="outline" size="sm" onClick={() => void copyToken()}>
                    Kopyala
                  </Button>
                </div>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-muted-foreground text-xs font-medium">Token kimliği</dt>
                    <dd className="font-mono text-xs">{issued.id}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground text-xs font-medium">Geçerlilik sonu</dt>
                    <dd>
                      {formatDateTime(issued.expiresAt)}
                      <span className="text-muted-foreground text-xs">
                        {" "}
                        ({formatRelative(issued.expiresAt)})
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground text-xs font-medium">Site</dt>
                    <dd>{issued.site ?? "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground text-xs font-medium">Etiketler</dt>
                    <dd className="flex flex-wrap gap-1">
                      {(issued.tags ?? []).map((tag) => (
                        <Badge key={tag} tone="muted">
                          {tag}
                        </Badge>
                      ))}
                      {(issued.tags ?? []).length === 0 ? "—" : null}
                    </dd>
                  </div>
                </dl>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setIssued(null)}>
                    Ekrandan kaldır
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-muted-foreground flex h-full min-h-40 flex-col items-center justify-center gap-2 text-center text-sm">
                <span
                  aria-hidden="true"
                  className="bg-muted flex h-12 w-12 items-center justify-center rounded-full text-xl"
                >
                  🔑
                </span>
                <p>Henüz token üretilmedi.</p>
                <p className="max-w-xs text-xs">
                  Soldaki formu doldurup <strong>Token üret</strong> dediğinizde gizli
                  değer burada bir kez görüntülenir.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Agent tarafında kullanım</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="text-muted-foreground list-decimal space-y-1 pl-5 text-sm">
            <li>
              Agent yapılandırmasındaki <code className="font-mono text-xs">enrollmentToken</code>{" "}
              alanına bu değeri yazın.
            </li>
            <li>
              Agent ilk açılışta <code className="font-mono text-xs">POST /enroll</code> ile
              kaydolur; sunucu cihaz sertifikasını (mTLS) döndürür.
            </li>
            <li>
              Token kayıttan sonra agent yapılandırmasından silinir — her token{" "}
              <strong>tek kullanımlıktır</strong>.
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}